import React, { Component } from 'react';
import { flushSync } from 'react-dom';
import classNameBind from 'classnames/bind';
import { ResponsiveContainer } from 'recharts';
import { withTranslation } from '../Translator';
import Message from '../Message';
import Spinner from '../Spinner';
import Text from '../Text';
import LineChartContent from './components/LineChartContent';
import BarChartContent from './components/BarChartContent';
import PieChartContent from './components/PieChartContent';
import generateLineColors from '../../utils/methods/generateLineColors';
import uuid from '../../utils/methods/uuid';
import styles from './Chart.module.css';

const bindClasses = classNameBind.bind(styles);

class Chart extends Component {
  constructor() {
    super();
    this.state = {
      left: 0,
      right: 0,
      activeIndex: -1,
      animationEndedKey: 0,
    };

    this.setMargins = this.setMargins.bind(this);

    this.chartId = uuid();
  }

  setMargins(leftMargin, rightMargin) {
    const { left, right } = this.state;
    const newState = {};

    if (leftMargin > left) {
      newState.left = leftMargin;
    }

    if (rightMargin > right) {
      newState.right = rightMargin;
    }

    if (newState.left || newState.right) {
      this.setState(newState);
    }
  }

  onPieEnter = (_, index) => {
    if (this.state.animationEndedKey) {
      this.setState({ activeIndex: index });
    }
  };

  onPieLeave = () => {
    if (this.state.animationEndedKey) {
      this.setState({ activeIndex: -1 });
    }
  };

  onAnimationEnd = (key) => {
    flushSync(() => { this.setState({ animationEndedKey: key }); });
  }

  render() {
    const {
      t,
      type,
      framed,
      data,
      seriesConfig,
      dateOnXAxis,
      isLoading,
      loadingProgress,
      loadingStatus,
      message,
      toolbar,
      messageType,
      labelX,
      labelSeries,
      labelSegment,
      syncId,
      compareMode,
      height,
      debounce,
      pieChartUnit,
      pieChartLabelThreshold,
      customPaletteSize,
      additionalMessage,
      disableAnimations,
      referenceLine,
      showWhiteSpace,
      selectedAnnotationName,
      onClick,
      withAnnotations,
      smallerTooltip,
      hideTooltip,
      withNowElement,
      hiddenData,
    } = this.props;
    const renderChart = (total) => {
      const lineColors = generateLineColors(
        customPaletteSize > -1 ? customPaletteSize : seriesConfig.length,
      );
      switch (type) {
        case 'bar':
        case 'barStacked':
        case 'barVertical':
        case 'barStackedVertical':
          return BarChartContent({
            framed: framed !== 'none',
            data,
            seriesConfig,
            dateOnXAxis,
            isStacked: type.indexOf('Stacked') > -1,
            isVertical: type.indexOf('Vertical') > -1,
            labelX,
            labelSeries,
            syncId,
            compareMode,
            lineColors,
            disableAnimations,
            t,
            referenceLine,
            showWhiteSpace,
            selectedAnnotationName,
            withAnnotations,
            onClick,
            smallerTooltip,
            hideTooltip,
            withNowElement,
            chartId: this.chartId,
            hiddenData,
          });
        case 'pie':
          return PieChartContent({
            data,
            labelSegment,
            compareMode,
            seriesConfig,
            pieChartUnit,
            pieChartLabelThreshold,
            total,
            setMargins: this.setMargins,
            leftMargin: this.state.left,
            rightMargin: this.state.right,
            t,
            showWhiteSpace,
            hideTooltip,
            syncId,
            activeIndex: this.state.activeIndex,
            onPieEnter: this.onPieEnter,
            onPieLeave: this.onPieLeave,
            onAnimationEnd: this.onAnimationEnd,
            animationEndedKey: this.state.animationEndedKey,
            disableAnimations,
          });
        case 'dots':
          return LineChartContent({
            framed: framed !== 'none',
            data,
            seriesConfig,
            dateOnXAxis,
            labelX,
            labelSeries,
            syncId,
            compareMode,
            lineColors,
            t,
            disableAnimations,
            referenceLine,
            dotsOnly: true,
            showWhiteSpace,
            selectedAnnotationName,
            withAnnotations,
            onClick,
            smallerTooltip,
            hideTooltip,
          });
        case 'line':
        default:
          return LineChartContent({
            framed: framed !== 'none',
            data,
            seriesConfig,
            dateOnXAxis,
            labelX,
            labelSeries,
            syncId,
            compareMode,
            lineColors,
            t,
            disableAnimations,
            referenceLine,
            showWhiteSpace,
            selectedAnnotationName,
            withAnnotations,
            onClick,
            smallerTooltip,
            hideTooltip,
            withNowElement,
            chartId: this.chartId,
          });
      }
    };

    const renderTopPanel = () => (
      toolbar
        ? (
          <div
            className={bindClasses('top-panel')}
            data-ppuc-id="Chart.topPanel"
          >
            {toolbar}
          </div>
        )
        : null
    );

    const renderChartContainer = () => {
      const isPie = type === 'pie';
      const noData = data.length === 0;
      const total = isPie
        ? data.reduce((accumulator, item) => accumulator + item.value, 0)
        : null;

      return noData || total === 0
        ? (
          <div
            className={bindClasses('zero-state', isPie ? 'pie-chart-mock' : 'line-chart-mock')}
            style={{ height }}
            data-ppuc-id="Chart.zeroState"
          >
            <Message
              message={noData ? message : t('Chart.message.zero-total-pie')}
              messageType={noData ? messageType : 'info'}
              centerVertical
              centerHorizontal
              vertical
              box
              additionalMessage={additionalMessage}
            />
          </div>
        )
        : (
          <div>
            {renderTopPanel()}
            <ResponsiveContainer height={height} debounce={debounce}>
              {renderChart(total)}
            </ResponsiveContainer>
          </div>
        );
    };

    return (
      <div
        className={bindClasses('wrapper', type)}
        style={{ minWidth: `calc(100px + ${this.state.left}px + ${this.state.right}px)` }}
        data-ppuc-id="Chart"
      >
        <div
          className={bindClasses('chart', `framed-${framed}`)}
          data-ppuc-id="Chart.content"
        >
          {isLoading
            ? (
              <div className={bindClasses('spinner')} style={{ height }}>
                <Spinner className="large" progress={loadingProgress} />
                <Text type="info">{loadingStatus}</Text>
              </div>
            )
            : renderChartContainer()
          }
        </div>
      </div>
    );
  }
}

Chart.defaultProps = {
  type: 'line',
  framed: 'full',
  data: [],
  seriesConfig: [],
  dateOnXAxis: false,
  isLoading: false,
  loadingProgress: -1,
  loadingStatus: '',
  message: '',
  messageType: 'error',
  toolbar: null,
  labelX: '',
  labelSeries: '',
  labelSegment: null,
  syncId: '',
  compareMode: [],
  height: 300,
  debounce: 0,
  pieChartUnit: null,
  pieChartLabelThreshold: 4,
  customPaletteSize: -1,
  additionalMessage: null,
  disableAnimations: false,
  showWhiteSpace: false,
  selectedAnnotationName: '',
  onClick: () => {},
  withAnnotations: false,
  smallerTooltip: false,
  hideTooltip: false,
  withNowElement: false,
  hiddenData: [],
};

export { Chart };

export default withTranslation('components')(Chart);
